import { useState, type MouseEvent } from 'react';
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import {
    MoreVert as MoreIcon,
    DeleteOutlineOutlined as DeleteIcon,
    EditOutlined as EditIcon,
    Visibility as PreviewIcon,
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import { toast } from 'react-toastify';
import type { DmAutomationDto } from '@/types/dmAutomation';
import { useDeleteAutomation, useToggleAutomation } from '@/hooks/queries/dm-automation';
import { useInstagramMediaByIds } from '@/hooks/queries/instagram';
import { dialogOpen } from '@/modules/MuiDialog';
import {
    ACCENT,
    ACCENT_TINT,
    TRIGGER_COLOR,
    TRIGGER_ICON,
    TRIGGER_LABEL,
    calcRate,
    formatDate,
    parseKeywords,
    parseTargetPostIds,
} from './_shared';

interface Props {
    automation: DmAutomationDto;
    onEdit?: (automation: DmAutomationDto) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const StatBlock = ({ value, label }: { value: string | number; label: string }) => (
    <Box sx={{ textAlign: 'center', minWidth: 56 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Box>
);

const AutomationCard = ({ automation, onEdit, isSelected = false, onSelect }: Props) => {
    const toggle = useToggleAutomation();
    const remove = useDeleteAutomation();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const TriggerIcon = TRIGGER_ICON[automation.triggerType];
    const triggerColor = TRIGGER_COLOR[automation.triggerType];
    const keywords = parseKeywords(automation.keyword);
    const targetIds = parseTargetPostIds(automation.targetPost);
    const isSpecificTarget = automation.targetPost !== 'ALL' && targetIds.length > 0;
    const targetLabel = isSpecificTarget ? `게시물 ${targetIds.length}개` : '모든 게시물';
    // 선택한 게시물 썸네일 (특정 게시물일 때만 조회)
    const targetMediaQ = useInstagramMediaByIds(isSpecificTarget ? targetIds : [], { enabled: isSpecificTarget });
    const targetMedia = targetMediaQ.data?.items ?? [];

    const openRate = calcRate(automation.stats.openedCount, automation.stats.sentCount);
    const clickRate = calcRate(automation.stats.clickedCount, automation.stats.sentCount);

    const handleToggle = (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.stopPropagation();
        const next = ev.target.checked;
        toggle.mutate(
            { id: automation.id, isActive: next },
            {
                onError: (err: any) => {
                    toast.error(err?.data?.message || '상태 변경에 실패했어요');
                },
            },
        );
    };

    const handleMenuOpen = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        setMenuAnchor(ev.currentTarget);
    };
    const handleMenuClose = () => setMenuAnchor(null);

    const handleEdit = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        handleMenuClose();
        onEdit?.(automation);
    };

    const handleDelete = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        handleMenuClose();
        dialogOpen({
            message: `"${automation.name}" 자동화를 삭제할까요?<br/>삭제하면 더 이상 자동 응답이 발송되지 않아요. 발송된 이력은 그대로 보존돼요.<br/><br/><b>※ 삭제해도 사용한 이용권은 복구되지 않습니다.</b>`,
            confirmText: '삭제',
            closeText: '취소',
            isAllowHtml: true,
            onConfirmed: async () => {
                try {
                    await remove.mutateAsync(automation.id);
                    toast.success('자동화를 삭제했어요');
                } catch (err: any) {
                    toast.error(err?.data?.message || '삭제에 실패했어요');
                }
            },
        });
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                opacity: automation.isActive ? 1 : 0.65,
                borderColor: isSelected ? triggerColor.main : 'divider',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? triggerColor.tint : 'background.paper',
                transition: 'border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                willChange: 'transform',
                '&:hover': {
                    borderColor: triggerColor.main,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                },
            }}
        >
            <CardActionArea
                disabled={!onSelect}
                onClick={() => onSelect?.()}
                sx={{ cursor: onSelect ? 'pointer' : 'default', borderRadius: 3 }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: triggerColor.tint,
                            color: triggerColor.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <TriggerIcon />
                    </Box>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }} noWrap>
                            {automation.name}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 0.75,
                                rowGap: 0.5,
                            }}
                        >
                            <Chip
                                size="small"
                                label={TRIGGER_LABEL[automation.triggerType]}
                                sx={{
                                    height: 20,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: triggerColor.tint,
                                    color: triggerColor.main,
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                            {keywords.slice(0, 4).map((kw) => (
                                <Chip
                                    key={kw}
                                    label={kw}
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        bgcolor: ACCENT_TINT,
                                        color: ACCENT,
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            ))}
                            {keywords.length > 4 && (
                                <Typography variant="caption" color="text.secondary">
                                    +{keywords.length - 4}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                · {targetLabel} ·
                            </Typography>
                            {isSpecificTarget && targetMedia.length > 0 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    {targetMedia.slice(0, 3).map((tm) => {
                                        const thumb = tm.thumbnailUrl || tm.mediaUrl;
                                        return (
                                            <Box
                                                key={tm.id}
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: 0.75,
                                                    flexShrink: 0,
                                                    bgcolor: 'action.hover',
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                    backgroundImage: thumb ? `url("${thumb}")` : 'none',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                }}
                                            />
                                        );
                                    })}
                                    {targetIds.length > 3 && (
                                        <Typography variant="caption" color="text.secondary">
                                            외 {targetIds.length - 3}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                만든 날 {formatDate(automation.createdAt)}
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}>
                        <StatBlock value={automation.stats.sentCount.toLocaleString()} label="발송" />
                        <StatBlock value={`${openRate}%`} label="오픈" />
                        <StatBlock value={`${clickRate}%`} label="클릭" />
                    </Stack>

                    {isSelected && (
                        <Chip
                            size="small"
                            icon={<PreviewIcon sx={{ fontSize: 12 }} />}
                            label="미리보기 중"
                            sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 10,
                                bgcolor: triggerColor.main,
                                color: '#fff',
                                '& .MuiChip-icon': { color: '#fff' },
                            }}
                        />
                    )}

                    <Chip
                        size="small"
                        label={automation.isActive ? '활성' : '일시정지'}
                        sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 10,
                            bgcolor: automation.isActive ? 'rgba(76,175,80,0.12)' : 'rgba(0,0,0,0.06)',
                            color: automation.isActive ? '#2e7d32' : 'text.secondary',
                        }}
                    />

                    <Switch
                        checked={automation.isActive}
                        onChange={handleToggle}
                        onClick={(ev) => ev.stopPropagation()}
                        disabled={toggle.isPending}
                        size="small"
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT },
                        }}
                    />

                    <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                        aria-label="more"
                    >
                        <MoreIcon fontSize="small" />
                    </IconButton>
                </Box>
            </CardActionArea>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                onClick={(ev) => ev.stopPropagation()}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 140 } }}
            >
                {onEdit && (
                    <MenuItem onClick={handleEdit} sx={{ cursor: 'pointer' }}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        수정
                    </MenuItem>
                )}
                {onEdit && <Divider sx={{ my: 0.5 }} />}
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main', cursor: 'pointer' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    삭제
                </MenuItem>
            </Menu>
        </Card>
    );
};

export default AutomationCard;
